import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '@/lib/axios';
import { PostCard } from './components/PostCard';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2 } from 'lucide-react';
import type { SocialPost } from './types';
import { getPostLookupError } from './post-lookup-error';

export default function CommunityPostPage() {
  const { communityId, postId } = useParams<{ communityId: string; postId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [post, setPost] = useState<SocialPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const handleBack = useCallback(() => {
    if (location.key !== 'default') {
      navigate(-1);
      return;
    }
    if (communityId) {
      navigate(`/${communityId}/dashboard?view=social`, { replace: true });
      return;
    }
    navigate('/dashboard', { replace: true });
  }, [location.key, navigate, communityId]);

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await axiosInstance.get(`/api/posts/${postId}`);
        setPost(data);
      } catch (err: unknown) {
        console.error(err);
        setError(getPostLookupError(err));
        setPost(null);
      } finally {
        setLoading(false);
      }
    };

    if (postId) fetchPost();
  }, [postId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="flex flex-col items-center justify-center py-24 px-4 text-center">
        <h2 className="text-xl font-semibold font-outfit mb-4">{error || 'Post not found'}</h2>
        <Button onClick={handleBack} variant="outline" className="rounded-xl">
          <ArrowLeft className="mr-2 h-4 w-4" /> Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-4 pb-12">
      <div className="flex items-center gap-2">
        <Button onClick={handleBack} variant="ghost" size="icon" className="-ml-2 rounded-xl">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <span className="font-semibold font-outfit text-lg">Post</span>
      </div>
      <PostCard post={post} communityId={communityId} onDelete={handleBack} />
    </div>
  );
}
